import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import {
  Building2, LogOut, User,
  LayoutDashboard, ShoppingCart, Package, Receipt, FileText, UserCircle,
} from 'lucide-react';
import { PORTAL_API } from './constants';
import { SupplierLogin, SupplierForgotPassword, SupplierRegister, SupplierTotpSetup, SupplierTotpVerify, SupplierPasswordChange } from './SupplierAuth';
import { SupplierDashboardTab } from './SupplierDashboardTab';
import { SupplierOrdersTab } from './SupplierOrdersTab';
import { SupplierItemsTab } from './SupplierItemsTab';
import { SupplierInvoiceAddTab, SupplierInvoiceApprovedTab, SupplierInvoicePendingTab, InvoiceDetailModal } from './SupplierInvoicesTab';
import { SupplierRfxPublicTab, SupplierMyRfxTab } from './SupplierRfxTab';
import { SupplierMyDetails } from './SupplierMyDetails';

export default function SupplierPortalPage() {
  const [view, setView] = useState('login');
  const [token, setToken] = useState(sessionStorage.getItem('supplier_token') || '');
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(false);

  // Branding
  const [branding, setBranding] = useState({ company_name: '', has_logo: false, logo_url: null });
  useEffect(() => { api.get('/api/system/public/branding').then(r => setBranding(r.data)).catch(() => {}); }, []);

  const portalTitle = branding.company_name ? `${branding.company_name} Supplier Portal` : 'Supplier Portal';
  const logoSrc = branding.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}` : null;

  const Footer = () => (
    <div className="text-center py-4 mt-6">
      <p className="text-[10px] text-slate-400">
        &copy; {new Date().getFullYear()} Eyvo eProcurement. All rights reserved.
      </p>
      <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors">
        Powered by Eyvo eProcurement
      </a>
    </div>
  );

  // Auth flow state
  const [authExtra, setAuthExtra] = useState({});

  // Dashboard
  const [profile, setProfile] = useState(null);
  const [dashData, setDashData] = useState(null);

  // Portal sections state
  const [portalTab, setPortalTab] = useState('dashboard');
  const [portalStats, setPortalStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [invoiceData, setInvoiceData] = useState({ data: [], orders: [] });
  const [orderSearch, setOrderSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [viewInvoice, setViewInvoice] = useState(null);
  const [myRfx, setMyRfx] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [events, setEvents] = useState([]);
  const [dashDateFrom, setDashDateFrom] = useState(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10); });
  const [dashDateTo, setDashDateTo] = useState(() => new Date().toISOString().slice(0, 10));

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const loadEvents = useCallback(async () => {
    if (!token) return;
    try {
      const r = await api.get(`${PORTAL_API}/my-events`, { headers: { Authorization: `Bearer ${token}` } });
      setEvents(r.data.data || []);
    } catch { toast.error('Failed to load events'); }
  }, [token]);

  const openEventDetail = async (ref) => {
    try {
      const r = await api.get(`${PORTAL_API}/event/${ref}`, { headers: { Authorization: `Bearer ${token}` } });
      setEventDetail(r.data);
      setSelectedEvent(ref);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to load event')); }
  };

  useEffect(() => {
    if (token) loadDashboard();
  }, [token]); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'OPEN_EVENT' && e.data?.event_ref) {
        openEventDetail(e.data.event_ref);
        setPortalTab('rfx-mine');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [token]); // eslint-disable-line

  useEffect(() => { if (view === 'dashboard') loadEvents(); }, [view, loadEvents]);

  const loadPortalData = async (tab) => {
    try {
      const hdr = { headers: { Authorization: `Bearer ${token}` } };
      if (tab === 'dashboard') {
        const r = await api.get(`${PORTAL_API}/portal-stats?from=${dashDateFrom}&to=${dashDateTo}`, hdr);
        setPortalStats(r.data);
      } else if (tab === 'orders') {
        const r = await api.get(`${PORTAL_API}/my-orders?search=${orderSearch}`, hdr);
        setOrders(r.data.data || []);
      } else if (tab === 'items') {
        const r = await api.get(`${PORTAL_API}/my-items?search=${itemSearch}`, hdr);
        setItems(r.data.data || []);
      } else if (tab === 'invoices-add' || tab === 'invoices-approved' || tab === 'invoices-pending') {
        const r = await api.get(`${PORTAL_API}/my-invoices`, hdr);
        setInvoiceData(r.data);
      } else if (tab === 'rfx-public') {
        loadEvents();
      } else if (tab === 'rfx-mine') {
        const r = await api.get(`${PORTAL_API}/my-rfx`, hdr);
        setMyRfx(r.data.data || []);
      }
    } catch { }
  };

  useEffect(() => { if (view === 'dashboard') loadPortalData(portalTab); }, [view, portalTab]); // eslint-disable-line

  const loadDashboard = async () => {
    try {
      const [pRes, dRes] = await Promise.all([
        api.get(`${PORTAL_API}/profile`, { headers: { Authorization: `Bearer ${token}` } }),
        api.get(`${PORTAL_API}/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProfile(pRes.data);
      setDashData(dRes.data);
      setSupplier(dRes.data);
      setView('dashboard');
    } catch {
      sessionStorage.removeItem('supplier_token');
      setToken('');
      setView('login');
    }
  };

  const handleLoginSuccess = (accessToken, supplierData) => {
    sessionStorage.setItem('supplier_token', accessToken);
    setToken(accessToken);
    if (supplierData) setSupplier(supplierData);
    setView('dashboard');
    toast.success('Logged in successfully');
  };

  const handleViewChange = (newView, extra = {}) => {
    setAuthExtra(extra);
    setView(newView);
  };

  const logout = () => {
    sessionStorage.removeItem('supplier_token');
    setToken('');
    setSupplier(null);
    setProfile(null);
    setView('login');
  };

  // ── Auth views ──
  if (view === 'login') return <SupplierLogin branding={branding} logoSrc={logoSrc} portalTitle={portalTitle} Footer={Footer} onViewChange={handleViewChange} onLoginSuccess={handleLoginSuccess} />;
  if (view === 'forgot_password') return <SupplierForgotPassword onViewChange={handleViewChange} />;
  if (view === 'register') return <SupplierRegister branding={branding} logoSrc={logoSrc} Footer={Footer} onViewChange={handleViewChange} />;
  if (view === 'totp_setup') return <SupplierTotpSetup totpUri={authExtra.totpUri} totpSupplierId={authExtra.totpSupplierId} onLoginSuccess={(t) => handleLoginSuccess(t)} />;
  if (view === 'totp_verify') return <SupplierTotpVerify tempToken={authExtra.tempToken} onLoginSuccess={(t) => handleLoginSuccess(t)} />;
  if (view === 'password_change') return <SupplierPasswordChange pwTempToken={authExtra.pwTempToken} onViewChange={handleViewChange} />;

  // ── FULL SUPPLIER PORTAL ──
  const portalCurrency = branding.system_currency || 'USD';
  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'orders', label: 'My Orders', icon: ShoppingCart },
    { id: 'items', label: 'Items', icon: Package },
    { id: 'invoices', label: 'My Invoices', icon: Receipt, children: [
      { id: 'invoices-add', label: 'Add Invoice' },
      { id: 'invoices-approved', label: 'Approved Invoices' },
      { id: 'invoices-pending', label: 'Pending Invoices' },
    ]},
    { id: 'rfx', label: 'RFx', icon: FileText, children: [
      { id: 'rfx-public', label: 'RFx Public View' },
      { id: 'rfx-mine', label: 'My RFx' },
    ]},
    { id: 'details', label: 'My Details', icon: UserCircle },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {logoSrc ? <img src={logoSrc} alt={branding.company_name} className="h-8 object-contain" /> : <Building2 className="h-6 w-6" />}
          <div>
            <h1 className="text-sm font-bold">{portalTitle}</h1>
            <p className="text-[10px] text-slate-400">{profile?.company_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-slate-300"><User className="h-3 w-3 inline mr-1" />{profile?.contact_name}</span>
          <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white h-7" onClick={logout}>
            <LogOut className="h-3.5 w-3.5 mr-1" /> Logout
          </Button>
        </div>
      </div>

      <div className="flex flex-1">
        {/* Sidebar */}
        <div className="w-48 bg-white border-r flex-shrink-0">
          <nav className="py-2">
            {sidebarItems.map(item => (
              <div key={item.id}>
                <button onClick={() => { setPortalTab(item.children ? item.children[0].id : item.id); loadPortalData(item.children ? item.children[0].id : item.id); }}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                    (portalTab === item.id || (item.children && item.children.some(c => c.id === portalTab)))
                      ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'
                  }`} data-testid={`portal-nav-${item.id}`}>
                  <item.icon className="w-4 h-4" /> {item.label}
                </button>
                {item.children && (portalTab === item.id || item.children.some(c => c.id === portalTab)) && (
                  <div className="ml-6 border-l">
                    {item.children.map(child => (
                      <button key={child.id} onClick={() => { setPortalTab(child.id); loadPortalData(child.id); }}
                        className={`w-full text-left px-4 py-1.5 text-xs transition-colors ${
                          portalTab === child.id ? 'text-indigo-700 font-semibold bg-indigo-50/50' : 'text-slate-500 hover:text-slate-700'
                        }`} data-testid={`portal-nav-${child.id}`}>
                        {child.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          {portalTab === 'dashboard' && <SupplierDashboardTab portalStats={portalStats} dashDateFrom={dashDateFrom} setDashDateFrom={setDashDateFrom} dashDateTo={dashDateTo} setDashDateTo={setDashDateTo} onReload={() => loadPortalData('dashboard')} />}
          {portalTab === 'orders' && <SupplierOrdersTab orders={orders} orderSearch={orderSearch} setOrderSearch={setOrderSearch} onReload={() => loadPortalData('orders')} token={token} />}
          {portalTab === 'items' && <SupplierItemsTab items={items} itemSearch={itemSearch} setItemSearch={setItemSearch} onReload={() => loadPortalData('items')} token={token} />}
          {portalTab === 'invoices-add' && <SupplierInvoiceAddTab invoiceData={invoiceData} token={token} onReload={() => loadPortalData('invoices-add')} currency={portalCurrency} />}
          {portalTab === 'invoices-approved' && <SupplierInvoiceApprovedTab invoiceData={invoiceData} token={token} onViewInvoice={setViewInvoice} currency={portalCurrency} />}
          {portalTab === 'invoices-pending' && <SupplierInvoicePendingTab invoiceData={invoiceData} token={token} onViewInvoice={setViewInvoice} currency={portalCurrency} />}
          {portalTab === 'rfx-public' && <SupplierRfxPublicTab token={token} />}
          {portalTab === 'rfx-mine' && <SupplierMyRfxTab myRfx={myRfx} token={token} onOpenEvent={openEventDetail} selectedEvent={selectedEvent} eventDetail={eventDetail} onBack={() => { setSelectedEvent(null); loadPortalData('rfx-mine'); }} />}
          {portalTab === 'details' && <SupplierMyDetails token={token} PORTAL_API={PORTAL_API} />}
          <InvoiceDetailModal viewInvoice={viewInvoice} onClose={() => setViewInvoice(null)} />
        </div>
      </div>
    </div>
  );
}
